import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all active:scale-[0.98] motion-reduce:active:scale-100 motion-reduce:transition-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Almikia design-system.md section 4/6: the default button is solid
        // navy, never the brand gradient (that is opt-in, see "gradient").
        default: "bg-brand-navy text-white shadow-xs hover:bg-brand-navy-light",
        // Brand gradient primary action (opt-in only, e.g. the form Save
        // button) -- see adr/ADR-ee2f59a9-TASK-038-gradient-button-contrast.md
        // for the deepened yellow stop + scrim that keeps the white label
        // AA-compliant.
        gradient:
          "rounded-[12px] bg-[image:var(--gradient-brand-button)] text-white font-bold shadow-xs hover:brightness-110",
        // The guide's semantic red (#D6452B) does not clear 4.5:1 with a
        // white label; its darker "red-text" shade (#B3321C) does, so the
        // destructive button uses that instead, in both themes.
        destructive:
          "bg-brand-red-text text-white shadow-xs hover:bg-brand-red-text/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        // "Secondary/outline are white with the subtle #E6E1D8 border and
        // navy text" (design-system.md section 6). The text color is never
        // hardcoded: it inherits (currentColor) so the same button reads
        // correctly on the paper background and inside a navy container.
        outline:
          "border border-[#e6e1d8] bg-white shadow-xs hover:bg-brand-navy-10 dark:bg-white/5 dark:border-white/15 dark:hover:bg-white/10",
        secondary:
          "border border-[#e6e1d8] bg-white shadow-xs hover:bg-brand-navy-10 dark:bg-white/5 dark:border-white/15 dark:hover:bg-white/10",
        // Ghost/link never set their own text color: they inherit
        // currentColor, so they read as navy on the paper background (body's
        // default foreground is navy) and as light text inside a navy
        // header/sidebar (which overrides its own `color`), instead of
        // hardcoding navy and going invisible on a navy background.
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-size={size ?? "default"}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
